import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ChecklistFormData, ChecklistColumnConfig, DEFAULT_COLUMN_CONFIG } from "@/types/stockChecklist";
import { auditService } from "@/services/auditService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ColumnSelectionSection } from "./ColumnSelectionSection";
import { StockChecklistPasswordReveal, generateStockChecklistPassword } from "./StockChecklistPasswordPanel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2, Package, Search, X, Check, ChevronsUpDown, HelpCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useDebouncedValue } from "@/hooks/use-debounce";
import { cn } from "@/lib/utils";
import { fetchAllRows } from "@/lib/fetchAllRows";
import type { Json } from "@/integrations/supabase/types";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CreateChecklistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface Deposit {
  id: string;
  name: string;
  is_main: boolean;
}

// A7a-i: prévia calculada no banco pela mesma regra da criação
interface PreviewResult {
  count: number;
  in_open_conflicts: number;
  examples: { product: string; checklist: string }[];
}

interface SpotProduct {
  id: string;
  name: string;
  sku: string;
}

interface AtomicResult {
  success: boolean;
  error?: string;
  checklist_id?: string;
  access_token?: string;
  items_count?: number;
}

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateChecklistDialog = ({
  open,
  onOpenChange,
  onSuccess,
}: CreateChecklistDialogProps) => {
  const { company, user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [availablePriceTables, setAvailablePriceTables] = useState<Array<{ id: string; name: string }>>([]);
  const [productCount, setProductCount] = useState<number | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isCountingProducts, setIsCountingProducts] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<ChecklistColumnConfig>(DEFAULT_COLUMN_CONFIG);
  // A8-i: balanço aberto da empresa (a conferência do mesmo depósito nasce parte dele)
  const [openBalance, setOpenBalance] = useState<{ id: string; name: string; deposit_id: string; counts_valid_since: string } | null>(null);
  // A7b: contagem com senha (a senha é gerada pelo banco logo depois de criar e aparece uma vez)
  const [withPassword, setWithPassword] = useState(false);
  const [revealed, setRevealed] = useState<{ password: string; link: string } | null>(null);

  // Spot-check
  const [selectedSpecificProducts, setSelectedSpecificProducts] = useState<SpotProduct[]>([]);
  const [spotSearchOpen, setSpotSearchOpen] = useState(false);
  const [spotSearch, setSpotSearch] = useState("");
  const [spotResults, setSpotResults] = useState<SpotProduct[]>([]);
  const [spotLoading, setSpotLoading] = useState(false);
  const debouncedSpotSearch = useDebouncedValue(spotSearch, 300);

  const [formData, setFormData] = useState<ChecklistFormData>({
    name: "",
    description: "",
    filters: {
      categories: [],
      brands: [],
      locations: [],
      abc_curves: [],
      letterFilter: [],
      onlyMinStock: false,
      onlyZeroNegative: false,
    },
    deposit_id: undefined,
  });

  // ── Data loading ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open && company?.id) loadFiltersData();
  }, [open, company?.id]);

  const countGenRef = useRef(0);
  useEffect(() => {
    if (!open || !company?.id) return;
    const genId = ++countGenRef.current;
    const timer = setTimeout(() => countProducts(genId), 400);
    return () => clearTimeout(timer);
  }, [formData.filters, formData.deposit_id, selectedSpecificProducts, open, company?.id]);

  // Spot search
  useEffect(() => {
    if (!debouncedSpotSearch || !company?.id) { setSpotResults([]); return; }
    const search = async () => {
      setSpotLoading(true);
      try {
        const { data } = await supabase.rpc("search_products_fuzzy", {
          p_company_id:   company.id,
          p_search_term:  debouncedSpotSearch,
          p_product_type: null,
          p_limit:        20,
          p_offset:       0,
        } as Parameters<typeof supabase.rpc>[1]);
        setSpotResults(
          (data as { id: string; name: string; sku: string }[] ?? []).map((p) => ({
            id:   p.id,
            name: p.name,
            sku:  p.sku,
          }))
        );
      } catch {
        setSpotResults([]);
      } finally {
        setSpotLoading(false);
      }
    };
    search();
  }, [debouncedSpotSearch, company?.id]);

  const loadFiltersData = async () => {
    if (!company?.id) return;
    try {
      // A7a-i: marcas e locais em lotes (passam de 1.000 linhas); depósito principal vem escolhido
      // A8-i: types.ts só é regenerado depois do APPLY (tabela stock_balances): cast documentado
      const { data: balanceRow } = await (supabase as any)
        .from("stock_balances")
        .select("id, name, deposit_id, counts_valid_since")
        .eq("company_id", company.id)
        .eq("status", "open")
        .maybeSingle();
      setOpenBalance(balanceRow ?? null);

      const [catsRes, depositsRes, brandRows, locRows, priceTablesRes] = await Promise.all([
        supabase.from("equipment_categories").select("name").eq("company_id", company.id).eq("active", true).order("name"),
        supabase.from("deposits").select("id, name, is_main").eq("company_id", company.id).eq("active", true).order("name"),
        fetchAllRows((from, to) =>
          supabase.from("products").select("id, brand").eq("company_id", company.id).eq("active", true)
            .not("brand", "is", null).order("brand").order("id").range(from, to)),
        fetchAllRows((from, to) =>
          supabase.from("product_warehouse_stock").select("id, location").eq("company_id", company.id)
            .not("location", "is", null).order("location").order("id").range(from, to)),
        supabase.from("price_tables").select("*").eq("company_id", company.id).eq("active", true).order("display_order").order("name"),
      ]);
      setCategories((catsRes.data ?? []).map((c) => c.name));
      const deps = (depositsRes.data ?? []) as Deposit[];
      setDeposits(deps);
      setFormData((prev) => prev.deposit_id ? prev : { ...prev, deposit_id: deps.find((d) => d.is_main)?.id });
      setBrands([...new Set(brandRows.map((p) => p.brand).filter(Boolean))] as string[]);
      setLocations([...new Set(locRows.map((l) => l.location).filter(Boolean))] as string[]);
      // PRECO-PADRAO (I.160, resposta 2): a "Preço Padrão" não entra na lista de tabelas da conferência — o "Novo P.V."
      // já é o preço dela
      setAvailablePriceTables(
        ((priceTablesRes.data ?? []) as Array<{ id: string; name: string; is_default?: boolean }>)
          .filter((t) => !t.is_default)
          .map(({ id, name }) => ({ id, name })),
      );
    } catch (err) {
      console.error("Erro ao carregar filtros:", err);
    }
  };

  // ── ABC / Location pre-computation (passed to atomic RPC) ──────────────────
  const fetchAbcProductIds = async (): Promise<string[] | null> => {
    const selectedCurves = formData.filters.abc_curves ?? [];
    if (selectedCurves.length === 0 || !company?.id) return null;

    const { data, error } = await supabase.rpc("get_inventory_intelligence_details", {
      p_company_id: company.id,
      p_section:    "abc_curve",
      p_limit:      10000,
      p_offset:     0,
    } as Parameters<typeof supabase.rpc>[1]);
    if (error) throw new Error(`Falha ao buscar produtos da Curva ABC: ${error.message}`);

    const result = data as unknown as { data: { id: string; class: string }[] };
    const ids = (result?.data ?? [])
      .filter((item) => selectedCurves.includes(item.class as "A" | "B" | "C"))
      .map((item) => item.id);
    if (ids.length === 0) throw new Error(
      `Nenhum produto encontrado para as curvas ABC selecionadas (${selectedCurves.join(", ")}). Verifique os filtros.`
    );
    return ids;
  };

  // A7a-i: filtros como o banco lê (locais da linha do depósito escolhido; produtos específicos ignoram o resto)
  const buildFiltersPayload = () => ({
    ...formData.filters,
    specificProductIds: selectedSpecificProducts.map((p) => p.id),
    visible_columns:    visibleColumns,
  });

  // ── Product count preview (A7a-i: pelo banco, com os conflitos de conferência aberta) ──
  const countProducts = async (genId?: number) => {
    if (!company?.id) return;
    if (!formData.deposit_id) {
      setPreview(null);
      setProductCount(null);
      return;
    }
    try {
      setIsCountingProducts(true);
      setPreviewError(null);
      const abcIds = await fetchAbcProductIds();
      if (genId !== undefined && genId !== countGenRef.current) return;
      const { data, error } = await supabase.rpc("preview_stock_checklist", {
        p_deposit_id:             formData.deposit_id,
        p_filters:                buildFiltersPayload() as unknown as Json,
        p_restricted_product_ids: abcIds ?? undefined,
      });
      if (genId !== undefined && genId !== countGenRef.current) return;
      if (error) throw error;
      const typed = data as unknown as PreviewResult;
      setPreview(typed);
      setProductCount(typed.count);
    } catch (err) {
      console.error("Erro ao contar produtos:", err);
      setPreview(null);
      setProductCount(null);
      setPreviewError((err as { message?: string })?.message ?? "Erro ao calcular a prévia");
    } finally {
      if (genId === undefined || genId === countGenRef.current) setIsCountingProducts(false);
    }
  };

  // ── Submit: atomic RPC (A3) ─────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error("Informe o nome da conferência"); return; }
    if (!formData.deposit_id) { toast.error("Escolha o depósito da conferência"); return; }
    if (!company?.id || !user?.id) return;

    setIsLoading(true);

    let abcIds: string[] | null = null;
    try {
      abcIds = await fetchAbcProductIds();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao aplicar o filtro da Curva ABC");
      setIsLoading(false);
      return;
    }

    if (productCount === 0) {
      toast.error("Nenhum produto corresponde aos filtros selecionados. Ajuste os filtros antes de criar a conferência.");
      setIsLoading(false);
      return;
    }

    try {
      const filtersPayload = buildFiltersPayload();

      const { data: result, error: rpcError } = await supabase.rpc(
        "create_stock_checklist_atomic",
        {
          p_name:                   formData.name,
          p_description:            formData.description ?? null,
          p_filters:                filtersPayload,
          p_deposit_id:             formData.deposit_id,
          p_restricted_product_ids: abcIds ?? null,
        } as Parameters<typeof supabase.rpc>[1]
      );
      // A7a-i: recusas de negócio do banco (produto em outra conferência aberta, depósito) vão com a mensagem dele
      if (rpcError) {
        toast.error(rpcError.message || "Erro ao criar conferência");
        return;
      }

      const typed = result as unknown as AtomicResult;
      if (!typed.success) {
        toast.error(typed.error ?? "Erro ao criar conferência");
        return;
      }

      await auditService.logAction({
        action:     "CRIAÇÃO",
        module:     "ESTOQUE",
        entityType: "conferencia_estoque",
        entityId:   typed.checklist_id!,
        description: `Conferência "${formData.name}" criada com ${typed.items_count ?? 0} produtos`,
        details: {
          checklist_name: formData.name,
          deposit_id:     formData.deposit_id,
          filters:        filtersPayload,
          products_count: typed.items_count ?? 0,
        },
      });

      const link = `${window.location.origin}/conferencia/${typed.access_token}`;

      // A7b: com senha, a tela mostra a senha uma vez e a aba abre no clique de "Abrir conferência"
      if (withPassword) {
        try {
          const password = await generateStockChecklistPassword(typed.access_token!);
          toast.success(`Conferência criada com ${typed.items_count ?? 0} produtos e com senha.`);
          handleClose();
          onSuccess();
          setRevealed({ password, link });
          return;
        } catch (err) {
          toast.error(
            `Conferência criada, mas a senha não foi gerada: ${(err as { message?: string })?.message ?? "erro"}. ` +
            `Abra a conferência e use "Ligar senha".`
          );
        }
      }

      // m-CONF.11 (fecha o m-CONF.8): a contagem abre na mesma aba — a aba nova, aberta depois das esperas do banco, era
      // barrada pelo bloqueador de janelas; o botão "Abrir" do cartão segue abrindo em aba nova
      toast.success(`Conferência criada com ${typed.items_count ?? 0} produtos!`);
      handleClose();
      onSuccess();
      navigate(`/conferencia/${typed.access_token}`);
    } catch (err) {
      console.error("Erro ao criar conferência:", err);
      toast.error("Erro ao criar conferência");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      filters: { categories: [], brands: [], locations: [], abc_curves: [], letterFilter: [], onlyMinStock: false, onlyZeroNegative: false },
      deposit_id: undefined,
    });
    setSelectedSpecificProducts([]);
    setSpotSearch("");
    setVisibleColumns(DEFAULT_COLUMN_CONFIG);
    setProductCount(null);
    setPreview(null);
    setPreviewError(null);
    setWithPassword(false);
    onOpenChange(false);
  };

  // ── Filter helpers ──────────────────────────────────────────────────────────
  const handleCategoryToggle = (category: string) => {
    const current = formData.filters.categories ?? [];
    setFormData({ ...formData, filters: { ...formData.filters, categories: current.includes(category) ? current.filter((c) => c !== category) : [...current, category] } });
  };
  const handleSelectAllCategories = () => {
    const current = formData.filters.categories ?? [];
    setFormData({ ...formData, filters: { ...formData.filters, categories: current.length === categories.length ? [] : [...categories] } });
  };
  const handleBrandToggle = (brand: string) => {
    const current = formData.filters.brands ?? [];
    setFormData({ ...formData, filters: { ...formData.filters, brands: current.includes(brand) ? current.filter((b) => b !== brand) : [...current, brand] } });
  };
  const handleSelectAllBrands = () => {
    const current = formData.filters.brands ?? [];
    setFormData({ ...formData, filters: { ...formData.filters, brands: current.length === brands.length ? [] : [...brands] } });
  };
  const handleLocationToggle = (location: string) => {
    const current = formData.filters.locations ?? [];
    setFormData({ ...formData, filters: { ...formData.filters, locations: current.includes(location) ? current.filter((l) => l !== location) : [...current, location] } });
  };
  const handleSelectAllLocations = () => {
    const current = formData.filters.locations ?? [];
    setFormData({ ...formData, filters: { ...formData.filters, locations: current.length === locations.length ? [] : [...locations] } });
  };
  const handleAbcToggle = (curve: "A" | "B" | "C") => {
    const current = formData.filters.abc_curves ?? [];
    setFormData({ ...formData, filters: { ...formData.filters, abc_curves: current.includes(curve) ? current.filter((c) => c !== curve) : [...current, curve] } });
  };
  const handleLetterToggle = (letter: string) => {
    const current = formData.filters.letterFilter ?? [];
    setFormData({ ...formData, filters: { ...formData.filters, letterFilter: current.includes(letter) ? current.filter((l) => l !== letter) : [...current, letter] } });
  };
  const handleAddSpecificProduct = (product: SpotProduct) => {
    if (selectedSpecificProducts.some((p) => p.id === product.id)) return;
    setSelectedSpecificProducts((prev) => [...prev, product]);
    setSpotSearch("");
    setSpotSearchOpen(false);
  };
  const handleRemoveSpecificProduct = (id: string) => {
    setSelectedSpecificProducts((prev) => prev.filter((p) => p.id !== id));
  };

  const renderCheckboxList = (
    items: string[],
    selected: string[],
    onToggle: (item: string) => void,
    onSelectAll: () => void,
    allLabel = "Selecionar Todos"
  ) => (
    <div className="space-y-2 pt-2">
      <Button type="button" variant="ghost" size="sm" onClick={onSelectAll} className="h-7 text-xs">
        {selected.length === items.length ? "Desmarcar Todos" : allLabel}
      </Button>
      <ScrollArea className="h-40 border rounded-md">
        <div className="p-3 space-y-1">
          {items.map((item) => (
            <label key={item} className="flex items-center gap-3 cursor-pointer py-2 px-2 rounded hover:bg-muted transition-colors">
              <Checkbox checked={selected.includes(item)} onCheckedChange={() => onToggle(item)} />
              <span className="text-sm">{item}</span>
            </label>
          ))}
        </div>
      </ScrollArea>
    </div>
  );

  const letterFilter = formData.filters.letterFilter ?? [];
  const hasSpotCheck = selectedSpecificProducts.length > 0;

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Conferência de Estoque</DialogTitle>
          <DialogDescription>Crie uma conferência para verificar o estoque físico</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
          {/* LEFT — Basic info + spot-check */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome da Conferência *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Conferência Mensal Janeiro"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description ?? ""}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Observações sobre a conferência..."
                rows={2}
              />
            </div>

            {deposits.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Label>Depósito *</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>No principal entram todos os produtos ativos; nos veículos, só os que têm saldo ou movimento ali. O gás entra só como produto específico.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Select
                  value={formData.deposit_id ?? ""}
                  onValueChange={(v) => {
                    // A8-i: o filtro "não contados desde" vem do balanço; fora do depósito dele, sai
                    const { notCountedSince: _drop, ...rest } = formData.filters;
                    setFormData({
                      ...formData,
                      deposit_id: v,
                      filters: openBalance && v === openBalance.deposit_id ? formData.filters : rest,
                    });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Escolha o depósito" /></SelectTrigger>
                  <SelectContent>
                    {deposits.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}{d.is_main ? " (principal)" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* A8-i: parte de balanço e filtro "não contados desde" (vale também com produto específico) */}
            {openBalance && formData.deposit_id === openBalance.deposit_id && (
              <div className="space-y-2 p-3 rounded-lg border border-primary/30 bg-primary/5">
                <p className="text-sm font-medium">Esta conferência será parte do balanço "{openBalance.name}"</p>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    Só não contados desde {openBalance.counts_valid_since.split("-").reverse().join("/")}
                  </p>
                  <Switch
                    checked={formData.filters.notCountedSince === openBalance.counts_valid_since}
                    onCheckedChange={(checked) => {
                      const { notCountedSince: _drop, ...rest } = formData.filters;
                      setFormData({
                        ...formData,
                        filters: checked ? { ...rest, notCountedSince: openBalance.counts_valid_since } : rest,
                      });
                    }}
                  />
                </div>
              </div>
            )}

            {/* Spot-check */}
            <Card className="border-dashed">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Produtos Específicos (Spot-check)</Label>
                  {hasSpotCheck && (
                    <Badge variant="secondary" className="text-xs">{selectedSpecificProducts.length} selecionado(s)</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Micro-auditoria. Ignora os demais filtros.</p>

                <Popover open={spotSearchOpen} onOpenChange={setSpotSearchOpen} modal>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between text-muted-foreground">
                      <span className="flex items-center gap-2"><Search className="h-4 w-4" />Buscar produto por nome ou SKU...</span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[380px] p-0 z-[100]" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput placeholder="Digite o nome ou SKU..." value={spotSearch} onValueChange={setSpotSearch} />
                      <CommandList>
                        <CommandEmpty>{spotLoading ? "Buscando..." : "Nenhum produto encontrado"}</CommandEmpty>
                        <CommandGroup>
                          {spotResults.map((product) => {
                            const isSelected = selectedSpecificProducts.some((p) => p.id === product.id);
                            return (
                              <CommandItem key={product.id} value={product.id} onSelect={() => handleAddSpecificProduct(product)} disabled={isSelected}>
                                <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                                <span className="font-medium mr-2">{product.sku}</span>
                                <span className="truncate text-muted-foreground">{product.name}</span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {selectedSpecificProducts.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedSpecificProducts.map((product) => (
                      <Badge key={product.id} variant="outline" className="gap-1 pr-1">
                        <span className="font-medium">{product.sku}</span>
                        <span className="text-muted-foreground truncate max-w-[120px]">{product.name}</span>
                        <button onClick={() => handleRemoveSpecificProduct(product.id)} className="ml-1 rounded-full hover:bg-muted p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* RIGHT — Filters + Column selection */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <p className="text-sm font-semibold text-foreground">Filtros de Produtos</p>

              {hasSpotCheck && (
                <div className="p-2 rounded-md bg-muted text-xs text-muted-foreground">
                  ⚠️ Com produtos específicos selecionados, os filtros abaixo são ignorados.
                </div>
              )}

              <div className={cn("flex items-center justify-between p-3 border rounded-lg", hasSpotCheck && "opacity-50")}>
                <div>
                  <p className="text-sm font-medium">Apenas estoque zerado/negativo</p>
                  <p className="text-xs text-muted-foreground">Foca em corrigir furos de estoque</p>
                </div>
                <Switch
                  disabled={hasSpotCheck}
                  checked={formData.filters.onlyZeroNegative ?? false}
                  onCheckedChange={(checked) => setFormData({ ...formData, filters: { ...formData.filters, onlyZeroNegative: checked } })}
                />
              </div>

              <Accordion type="multiple" className={cn("w-full", hasSpotCheck && "opacity-50 pointer-events-none")}>
                {/* Letra */}
                <AccordionItem value="letter">
                  <AccordionTrigger className="text-sm font-medium">
                    Letra Inicial{letterFilter.length > 0 && <span className="ml-2 text-muted-foreground">({letterFilter.join(", ")})</span>}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-2">
                      <p className="text-xs text-muted-foreground">Filtre pela letra inicial do nome</p>
                      <div className="grid grid-cols-9 gap-1">
                        {ALPHABET.map((letter) => (
                          <Button key={letter} type="button" size="sm" variant={letterFilter.includes(letter) ? "default" : "outline"} className="h-8 w-8 p-0 text-xs font-medium" onClick={() => handleLetterToggle(letter)}>
                            {letter}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Curva ABC */}
                <AccordionItem value="abc">
                  <AccordionTrigger className="text-sm font-medium">
                    Curva ABC{(formData.filters.abc_curves ?? []).length > 0 && <span className="ml-2 text-muted-foreground">({formData.filters.abc_curves?.join(", ")})</span>}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="flex gap-3 pt-2">
                      {(["A", "B", "C"] as const).map((curve) => (
                        <label key={curve} className="flex items-center gap-2 cursor-pointer py-2 px-3 rounded border hover:bg-muted transition-colors">
                          <Checkbox checked={(formData.filters.abc_curves ?? []).includes(curve)} onCheckedChange={() => handleAbcToggle(curve)} />
                          <span className="text-sm font-medium">Curva {curve}</span>
                        </label>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Categorias */}
                {categories.length > 0 && (
                  <AccordionItem value="categories">
                    <AccordionTrigger className="text-sm font-medium">
                      Categorias{(formData.filters.categories ?? []).length > 0 && <span className="ml-2 text-muted-foreground">({formData.filters.categories?.length} sel.)</span>}
                    </AccordionTrigger>
                    <AccordionContent>{renderCheckboxList(categories, formData.filters.categories ?? [], handleCategoryToggle, handleSelectAllCategories)}</AccordionContent>
                  </AccordionItem>
                )}

                {/* Marcas */}
                {brands.length > 0 && (
                  <AccordionItem value="brands">
                    <AccordionTrigger className="text-sm font-medium">
                      Marcas{(formData.filters.brands ?? []).length > 0 && <span className="ml-2 text-muted-foreground">({formData.filters.brands?.length} sel.)</span>}
                    </AccordionTrigger>
                    <AccordionContent>{renderCheckboxList(brands, formData.filters.brands ?? [], handleBrandToggle, handleSelectAllBrands)}</AccordionContent>
                  </AccordionItem>
                )}

                {/* Localizações */}
                {locations.length > 0 && (
                  <AccordionItem value="locations">
                    <AccordionTrigger className="text-sm font-medium">
                      Localização / Prateleira{(formData.filters.locations ?? []).length > 0 && <span className="ml-2 text-muted-foreground">({formData.filters.locations?.length} sel.)</span>}
                    </AccordionTrigger>
                    <AccordionContent>{renderCheckboxList(locations, formData.filters.locations ?? [], handleLocationToggle, handleSelectAllLocations)}</AccordionContent>
                  </AccordionItem>
                )}

                {/* Colunas Visíveis (B4 inside) */}
                <AccordionItem value="columns">
                  <AccordionTrigger className="text-sm font-medium">Colunas Visíveis</AccordionTrigger>
                  <AccordionContent>
                    <div className="pt-2">
                      <ColumnSelectionSection
                        columns={visibleColumns}
                        onChange={setVisibleColumns}
                        availablePriceTables={availablePriceTables}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* Product count preview */}
        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
          <Package className="h-5 w-5 text-primary" />
          <div>
            {isCountingProducts ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <p className="text-sm text-muted-foreground">Calculando...</p>
              </div>
            ) : previewError ? (
              <p className="text-sm text-destructive">{previewError}</p>
            ) : productCount !== null ? (
              <>
                <p className="font-medium">{productCount} produtos</p>
                <p className="text-sm text-muted-foreground">serão incluídos na conferência</p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Escolha o depósito e os filtros</p>
            )}
          </div>
        </div>

        {!isCountingProducts && (preview?.in_open_conflicts ?? 0) > 0 && (
          <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-sm">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-amber-700 dark:text-amber-400">
                {preview!.in_open_conflicts} produto(s) já estão em conferência aberta deste depósito
              </p>
              <ul className="mt-1 text-xs text-muted-foreground">
                {preview!.examples.map((e) => (
                  <li key={`${e.product}-${e.checklist}`}>{e.product} — "{e.checklist}"</li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-muted-foreground">
                Finalize ou cancele aquela conferência, ou ajuste os filtros.
              </p>
            </div>
          </div>
        )}

        {/* A7b: contagem com senha (decisão 1; senha substitui a chave, não o login) */}
        <div className="flex items-start gap-3 p-3 rounded-lg border border-border">
          <Switch id="checklist-with-password" checked={withPassword} onCheckedChange={setWithPassword} />
          <div className="space-y-0.5">
            <Label htmlFor="checklist-with-password" className="cursor-pointer">Contagem com senha</Label>
            <p className="text-xs text-muted-foreground">
              Quem está logado sem a permissão de conferência poderá contar digitando uma senha de 6 números, gerada ao
              criar e mostrada uma única vez. Sem ver preço nem custo, e sem finalizar.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !productCount || !formData.deposit_id || (preview?.in_open_conflicts ?? 0) > 0}
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Criar Conferência
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <StockChecklistPasswordReveal
      password={revealed?.password ?? null}
      onClose={() => setRevealed(null)}
      extraAction={revealed ? { label: "Abrir conferência", onClick: () => window.open(revealed.link, "_blank") } : undefined}
    />
    </>
  );
};
